import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ShippingAddress } from '@api/model/shippingAddress';
import { UntilDestroy } from '@core/classes/untilDestroy.class';
import { FormStateService } from '@core/services/form-state.service';
import { cleanFormValue, hasFormValueChanged, omitEmptyFields } from '@core/utils';
import { PostalCodeValidator } from '@core/validators/validators';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMaskPipe, NgxMaskService } from 'ngx-mask';
import { takeUntil } from 'rxjs';
import { PbbFormFieldErrorComponent } from '../pbb-form-field-error/pbb-form-field-error.component';
import { StickyButtonDirective } from '@core/directives/btn-sticky.directive';
import { PbbButtonComponent } from '../pbb-button/pbb-button.component';

@Component({
  selector: 'pbb-address-edit',
  templateUrl: './address-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    PbbFormFieldErrorComponent,
    MatButtonModule,
    TranslateModule,
    StickyButtonDirective,
    PbbButtonComponent,
    NgxMaskPipe,
  ],
})
export class AddressEditComponent extends UntilDestroy implements OnChanges, OnDestroy {
  @Output() submit$ = new EventEmitter<ShippingAddress>();
  @Input() address?: ShippingAddress;
  @Input() isLoading = false;
  @Input() isDefaultAddress = false;
  @Input() isMerchantAddress = false;
  @Input() hasInvalidPostalCodeError = false;
  showRuralAddress = false;
  invalidPostalCode: string | null = null;
  addressForm: FormGroup = this.fb.group({
    recipient_name: ['', [Validators.required, Validators.maxLength(100)]],
    recipient_phone_number: ['', [Validators.pattern(/^\+?[0-9\-\s]+$/), this.phoneFormatValidator]],
    street_name: ['', [Validators.required, Validators.maxLength(100)]],
    apartment: ['', Validators.maxLength(20)],
    city: ['', [Validators.required, Validators.maxLength(50)]],
    province: ['', [Validators.required, Validators.maxLength(20)]],
    postal_code: ['', [Validators.required, PostalCodeValidator, Validators.maxLength(40)]],
    rural_postal_address: ['', Validators.maxLength(100)],
    country: [{ value: 'CA', disabled: true }, [Validators.required]],
    address_line1: [''],
  });

  hasFormValueChanged = false;

  constructor(
    private readonly fb: FormBuilder,
    private formStateService: FormStateService,
    private maskService: NgxMaskService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const controlKey of Object.keys(this.addressForm.controls)) {
      this.addressForm
        .get(controlKey)
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((x) => {
          this.addressForm.get(controlKey)?.markAsTouched();
        });
    }

    if (changes['address'] && changes['address'].currentValue !== changes['address'].previousValue) {
      this.initForm();
    } else {
      this.checkFormChanges();
    }
  }

  initForm() {
    const address = {
      recipient_name: this.address?.recipient_name,
      recipient_phone_number: this.formatRecipientPhoneNumber(this.address?.recipient_phone_number || ''),
      street_name: this.address?.street_name,
      apartment: this.address?.apartment,
      city: this.address?.city,
      province: this.address?.province,
      postal_code: this.maskService.applyMask(
        this.address?.postal_code ? this.address?.postal_code : '',
        'AAA AAA||S0S 0S0'
      ),
      rural_postal_address: this.address?.rural_postal_address,
      country: this.address?.country || 'CA',
      address_line1: this.address?.address_line1,
    };

    if(this.hasInvalidPostalCodeError && !this.invalidPostalCode) {
      this.invalidPostalCode = this.address?.postal_code || null;
      this.getControl('postal_code').addValidators(this.samePostalCodeValidator());
    }
    this.addressForm?.patchValue(address);
    this.addressForm.markAllAsTouched();
    this.checkFormChanges();
    if (this.address?.rural_postal_address) {
      this.toggleRural();
    }
  }

  submit() {
    this.formStateService.hasChange = false;
    this.submit$.emit(this.getShippingAddressFromForm());
  }

  getControl(formControlName: string): FormControl {
    return this.addressForm.get(formControlName) as FormControl;
  }
  toggleRural() {
    this.showRuralAddress = !this.showRuralAddress;
    const streetNameControl = this.getControl('street_name');
    const ruralAddressControlControl = this.getControl('rural_postal_address');
    if (this.showRuralAddress) {
      ruralAddressControlControl.addValidators(Validators.required);
      streetNameControl.removeValidators(Validators.required);
    } else {
      ruralAddressControlControl.setValue('');
      streetNameControl.addValidators(Validators.required);
      ruralAddressControlControl.removeValidators(Validators.required);
    }
    streetNameControl.updateValueAndValidity();
    ruralAddressControlControl.updateValueAndValidity();
  }

  checkFormChanges() {
    const initialValue = this.addressForm.value;
    this.addressForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.getControl('recipient_phone_number').setValue(this.formatRecipientPhoneNumber(val.recipient_phone_number), {
        emitEvent: false,
      });
      this.getControl('postal_code').setValue(
        this.maskService.applyMask(val.postal_code ? val.postal_code : '', 'AAA AAA||S0S 0S0'),
        { emitEvent: false }
      );
      this.formStateService.hasChange = hasFormValueChanged(initialValue, this.addressForm);
      this.hasFormValueChanged = hasFormValueChanged(initialValue, this.addressForm) || this.isMerchantAddress;
    });

    if (this.isMerchantAddress) {
      this.hasFormValueChanged = true;
    }
  }

  private formatRecipientPhoneNumber(value: string): string {
    if (!value) {
      return '';
    }

    let digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);

    if (!digits) {
      return '';
    }

    const areaCode = digits.slice(0, 3);
    const exchange = digits.slice(3, 6);
    const line = digits.slice(6, 10);

    let formatted = '+1';
    if (areaCode) {
      formatted += `-${areaCode}`;
    }
    if (exchange) {
      formatted += `-${exchange}`;
    }
    if (line) {
      formatted += `-${line}`;
    }

    return formatted;
  }

  private samePostalCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.invalidPostalCode) return null;
      const normalize = (v: string) => v.replaceAll(/\s/g, '').toUpperCase();
      return normalize(control.value || '') === normalize(this.invalidPostalCode)
        ? { samePostalCode: true }
        : null;
    };
  }

  private phoneFormatValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    return /^\+1-\d{3}-\d{3}-\d{4}$/.test(value) ? null : { phoneFormat: true };
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.formStateService.hasChange = false;
  }

  private getShippingAddressFromForm(): ShippingAddress {
    const addr = omitEmptyFields({
        ...(this.address || {}),
        ...this.addressForm?.value,
        country: this.address?.country || 'CA',
        street_number: ' ',
        apartment: cleanFormValue(this.addressForm?.value['apartment']),
        recipient_name: cleanFormValue(this.addressForm?.value['recipient_name']),
        recipient_phone_number: this.addressForm?.value['recipient_phone_number'],
        address_line1: cleanFormValue(this.addressForm?.value['address_line1']),
      });
    return {
      city: addr.city,
      province: addr.province,
      postal_code: addr.postal_code,
      country: addr.country,
      apartment: addr.apartment,
      street_name: addr.street_name,
      street_number: addr.street_number,
      rural_postal_address: addr.rural_postal_address,
      recipient_name: addr.recipient_name,
      recipient_phone_number: addr.recipient_phone_number,
      shipping_address_ref: this.address?.shipping_address_ref?.includes('merchant-address-') ? undefined : this.address?.shipping_address_ref,
      address_line1: addr.address_line1,
    } as ShippingAddress;
  }
}
